import { SafeImage } from "../common/SafeImage";
import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { Button } from "../common/Button";
import { Input } from "../common/Input";
import { Card } from "../common/Card";
import { CartItem, MealType } from "../../types/index";
import { Search, X, ChefHat } from "lucide-react";
import { toast } from "react-hot-toast";
import { getMenuByKitchenId } from "../../services/kitchen.service";

interface Kitchen {
  id: string;
  uid: string;
  kitchen_name: string;
  logoUrl?: string;
  cuisines?: string[];
  status?: string;
  subscriptionStatus?: string;
}

interface ItemSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: CartItem | CartItem[]) => void;
  kitchenId?: string;
  context?: {
    date: Date;
    meal: MealType;
    slot: number; // 1 for main, 2 for backup
  };
}

// Fetch all available kitchens (for subscriptions)
const fetchAllKitchens = async (): Promise<Kitchen[]> => {
  try {
    const q = query(
      collection(db, "kitchens"),
      where("subscriptionStatus", "==", "active")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          uid: doc.id,
          ...doc.data(),
        } as Kitchen)
    );
  } catch (error) {
    console.error("Error fetching kitchens:", error);
    throw error;
  }
};

// Fetch menu items using kitchen service (preference catalog)
const fetchKitchenMenuItems = async (kitchenId: string): Promise<CartItem[]> => {
  try {
    const menu = await getMenuByKitchenId(kitchenId);

    // Convert menuItems object to array
    const items = Object.entries(menu.menuItems || {}).map(
      ([id, item]: [string, any]) =>
        ({
          id,
          name: item.name,
          description: item.description,
          price: item.price,
          category: item.categories?.[0] || item.category,
          image: item.photoUrl || item.image,
          available: item.isAvailable !== false,
          kitchenId,
          kitchenName: menu.name,
          ...item,
        } as CartItem)
    );

    return items;
  } catch (error) {
    console.error("Error fetching kitchen menu:", error);
    throw error;
  }
};

export const ItemSelectorModal: React.FC<ItemSelectorModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  kitchenId: initialKitchenId,
  context,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedKitchenId, setSelectedKitchenId] = useState<string | undefined>(
    initialKitchenId
  );
  const [selectedItems, setSelectedItems] = useState<CartItem[]>([]);

  // Fetch all kitchens when no kitchenId is provided
  const {
    data: kitchens = [],
    isLoading: loadingKitchens,
  } = useQuery(["allKitchens"], fetchAllKitchens, {
    enabled: !initialKitchenId && isOpen,
  });

  // Determine which kitchen to use
  const activeKitchenId = initialKitchenId || selectedKitchenId;

  // Fetch menu items for the selected kitchen
  const {
    data: menuItems = [],
    isLoading,
    isError,
  } = useQuery(
    ["kitchenMenu", activeKitchenId],
    () =>
      activeKitchenId
        ? fetchKitchenMenuItems(activeKitchenId)
        : Promise.resolve([]),
    {
      enabled: !!activeKitchenId && isOpen,
    }
  );

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!searchQuery) return menuItems;
    const q = searchQuery.toLowerCase();
    return menuItems.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q) ||
        item.category?.toLowerCase().includes(q)
    );
  }, [menuItems, searchQuery]);

  // Handle toggle selection for multiple items
  const handleSelectItem = (item: CartItem) => {
    setSelectedItems((prev) => {
      const isSelected = prev.find((i) => i.id === item.id);
      if (isSelected) {
        // Remove item if already selected
        return prev.filter((i) => i.id !== item.id);
      } else {
        // Add item if not selected
        return [...prev, item];
      }
    });
  };

  // Confirm and add all selected items
  const handleConfirmSelection = () => {
    if (selectedItems.length === 0) {
      toast.error("Please select at least one item");
      return;
    }

    // Call onSelect with array of items
    onSelect(selectedItems);

    toast.success(`${selectedItems.length} item(s) added!`);
    resetAndClose();
  };

  // Clear all selections
  const handleClearAll = () => {
    setSelectedItems([]);
    toast("Selections cleared");
  };

  // Reset state and close modal
  const resetAndClose = () => {
    setSelectedItems([]);
    setSearchQuery("");
    setSelectedKitchenId(undefined);
    onClose();
  };

  const handleKitchenSelect = (kitchenId: string) => {
    setSelectedKitchenId(kitchenId);
    setSearchQuery("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <Card className="w-full sm:w-2/3 lg:w-1/2 max-h-[90vh] flex flex-col rounded-t-2xl sm:rounded-2xl shadow-2xl">
        {/* HEADER */}
        <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-orange-50 to-blue-50">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-800">
              Select {context?.meal.toUpperCase() || "MEAL"} Item
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {context?.slot === 1 ? "Main" : "Backup"} option
              {context?.date && (
                <> for {new Date(context.date).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}</>
              )}
            </p>
            {selectedItems.length > 0 && (
              <div className="text-sm text-orange-600 font-semibold mt-1">
                {selectedItems.length} item(s) selected
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors ml-2 flex-shrink-0"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* KITCHEN SELECTOR - Show only when no initial kitchenId */}
          {!initialKitchenId && !selectedKitchenId && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <ChefHat className="text-orange-600" size={20} />
                <h3 className="font-bold text-gray-700">Select a Kitchen</h3>
              </div>

              {loadingKitchens ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                  <p className="text-gray-500 mt-2">Loading kitchens...</p>
                </div>
              ) : kitchens.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg mb-2">
                    No kitchens available
                  </p>
                  <p className="text-xs text-gray-400">
                    There are no active kitchens at the moment
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto">
                  {kitchens.map((kitchen) => (
                    <button
                      key={kitchen.id}
                      onClick={() => handleKitchenSelect(kitchen.id)}
                      className="flex items-center gap-3 p-3 border rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-all group text-left"
                    >
                      <SafeImage
                          src={kitchen.logoUrl}
                          alt={kitchen.kitchen_name || "Kitchen"}
                          fallbackSrc="/ruchira-logo.png"
                          className="w-12 h-12 rounded-full object-cover bg-gray-100"
                        />
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-800 group-hover:text-orange-600">
                          {kitchen.kitchen_name}
                        </h4>
                        {kitchen.cuisines && kitchen.cuisines.length > 0 && (
                          <p className="text-xs text-gray-500">
                            {kitchen.cuisines.join(", ")}
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SHOW MENU WHEN KITCHEN IS SELECTED */}
          {activeKitchenId && (
            <>
              {/* KITCHEN INFO - Show selected kitchen name */}
              {!initialKitchenId && selectedKitchenId && (
                <div className="flex items-center justify-between bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2">
                    <ChefHat className="text-orange-600" size={16} />
                    <span className="text-sm font-semibold text-gray-700">
                      {
                        kitchens.find((k) => k.id === selectedKitchenId)
                          ?.kitchen_name
                      }
                    </span>
                  </div>
                  <button
                    onClick={() => setSelectedKitchenId(undefined)}
                    className="text-xs text-orange-600 hover:text-orange-700 underline"
                  >
                    Change Kitchen
                  </button>
                </div>
              )}

              {/* SEARCH BAR */}
              <div className="relative sticky top-0 bg-white z-10 pb-2">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <Input
                  placeholder="Search items, category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* LOADING STATE */}
              {isLoading && (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                  <p className="text-gray-500 mt-2">Loading menu items...</p>
                </div>
              )}

              {/* ERROR STATE */}
              {isError && (
                <div className="text-center py-12">
                  <p className="text-red-500 font-semibold">
                    Failed to load menu items
                  </p>
                  <p className="text-xs text-red-400 mt-1">Please try again</p>
                </div>
              )}

              {/* ITEMS GRID */}
              {!isLoading && !isError && filteredItems.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-4">
                  {filteredItems.map((item) => {
                    const isSelected = selectedItems.find(
                      (i) => i.id === item.id
                    );
                    return (
                      <div
                        key={item.id}
                        onClick={() => handleSelectItem(item)}
                        className={`text-left p-3 border-2 rounded-lg cursor-pointer transition-all group shadow-sm hover:shadow-md relative ${
                          isSelected
                            ? "border-orange-500 bg-orange-50"
                            : "border-gray-200 hover:border-orange-400 hover:bg-orange-50"
                        }`}
                      >
                        {/* Checkbox Indicator */}
                        <div className="flex items-start justify-between mb-2">
                          <div
                            className="w-5 h-5 rounded border-2 flex items-center justify-center bg-white transition-colors"
                            style={{
                              borderColor: isSelected ? "#f97316" : "#d1d5db",
                              backgroundColor: isSelected ? "#f97316" : "white",
                            }}
                          >
                            {isSelected && (
                              <span className="text-white font-bold text-sm">
                                ✓
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Image */}
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-24 object-cover rounded-md mb-2 bg-gray-200"
                          />
                        )}

                        {/* Details */}
                        <div className="space-y-1">
                          <h3 className="font-bold text-gray-800 text-sm truncate group-hover:text-orange-600">
                            {item.name}
                          </h3>
                          {item.category && (
                            <p className="text-xs text-gray-400">
                              {item.category}
                            </p>
                          )}
                          {item.description && (
                            <p className="text-xs text-gray-500 line-clamp-2">
                              {item.description}
                            </p>
                          )}
                        </div>

                        {/* Price & Availability */}
                        <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
                          <span className="font-bold text-orange-600 text-sm">
                            ₹{item.price?.toFixed(2)}
                          </span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                              item.available
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {item.available ? "Available" : "Not Available"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                !isLoading &&
                !isError && (
                  <div className="text-center py-12">
                    <p className="text-gray-500 text-lg">No items found</p>
                    {searchQuery && (
                      <p className="text-xs text-gray-400 mt-1">
                        Try a different search
                      </p>
                    )}
                  </div>
                )
              )}
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="border-t p-4 bg-gray-50 sticky bottom-0">
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>

            {selectedItems.length > 0 && (
              <Button
                variant="danger"
                onClick={handleClearAll}
                className="flex-1"
              >
                Clear All ({selectedItems.length})
              </Button>
            )}

            <Button
              variant="primary"
              onClick={handleConfirmSelection}
              disabled={selectedItems.length === 0}
              className="flex-1"
            >
              {selectedItems.length > 0
                ? `Add ${selectedItems.length} Item(s)`
                : "Select Items"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ItemSelectorModal;
